import { useState } from 'react';
import { Icons } from '@cleanpro/common-ui';
import { useGrowthOps } from '../hooks/useGrowthOps';
import { ACTION_TYPE_LABELS } from '../types';

export default function OptimizerAuditLog() {
  const { optimizerActions, loading, refreshOptimizerActions } = useGrowthOps();
  const [businessFilter, setBusinessFilter] = useState('');

  const filtered = businessFilter
    ? optimizerActions.filter((a) =>
        a.businessName.toLowerCase().includes(businessFilter.toLowerCase())
      )
    : optimizerActions;

  if (loading) {
    return <div className="h-64 animate-pulse rounded-lg bg-gray-100" />;
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Optimizer Audit Log ({filtered.length})
          </h3>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Icons.Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Filter by provider..."
                value={businessFilter}
                onChange={(e) => setBusinessFilter(e.target.value)}
                className="rounded-md border border-gray-300 py-1.5 pl-9 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <button
              onClick={() => refreshOptimizerActions()}
              className="rounded-md border border-gray-300 p-1.5 text-gray-400 hover:bg-gray-50 hover:text-gray-600"
              title="Refresh"
            >
              <Icons.RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-h-[600px] overflow-y-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="sticky top-0 bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Timestamp
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Provider
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Action
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Campaign
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Details
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">
                  No optimizer actions recorded.
                </td>
              </tr>
            ) : (
              filtered.map((action) => (
                <tr key={action.id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-500">
                    {new Date(action.createdAt).toLocaleString()}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                    {action.businessName}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <ActionBadge type={action.actionType} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-xs font-mono text-gray-500">
                    {action.campaignId
                      ? `${action.campaignId.slice(0, 8)}...`
                      : '—'}
                  </td>
                  <td className="max-w-xs truncate px-4 py-3 text-xs text-gray-500">
                    {formatDetails(action.details)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ActionBadge({ type }: { type: string }) {
  const colors: Record<string, string> = {
    pause: 'bg-yellow-100 text-yellow-800',
    resume: 'bg-green-100 text-green-800',
    error: 'bg-red-100 text-red-800',
    weekly_cap_reached: 'bg-orange-100 text-orange-800',
    wallet_check: 'bg-blue-100 text-blue-800',
    daily_reset: 'bg-gray-100 text-gray-600',
  };

  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
        colors[type] ?? 'bg-gray-100 text-gray-700'
      }`}
    >
      {ACTION_TYPE_LABELS[type] ?? type}
    </span>
  );
}

function formatDetails(details: Record<string, unknown>): string {
  if (!details || Object.keys(details).length === 0) return '—';
  const entries = Object.entries(details)
    .map(([k, v]) => `${k}: ${String(v)}`)
    .join(', ');
  return entries;
}
